/**
 * RealTimeMonitor Component
 * Displays live stream of LLM requests with SSE
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Alert,
  Button,
  Badge,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  FiberManualRecord as LiveIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { LLMRequest } from '../../services/llm-monitoring';

interface RealTimeMonitorProps {
  requests: LLMRequest[];
  enabled: boolean;
  onToggle: () => void;
}

const RealTimeMonitor: React.FC<RealTimeMonitorProps> = ({
  requests,
  enabled,
  onToggle
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new requests arrive
  useEffect(() => {
    if (listRef.current && enabled) {
      listRef.current.scrollTop = 0;
    }
  }, [requests, enabled]);

  const getModelShortName = (model: string) => {
    if (model.includes('phi-4')) return 'Phi-4';
    if (model.includes('claude')) return 'Claude';
    if (model.includes('gpt')) return 'GPT';
    return model.split('/').pop() || model;
  };

  const formatCost = (cost: number | string) => {
    const numCost = Number(cost);
    if (numCost < 0.0001) return '<$0.0001';
    return `$${numCost.toFixed(4)}`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Real-Time Monitor
          </Typography>
          {enabled && (
            <Badge badgeContent={<LiveIcon sx={{ fontSize: 12 }} />} color="error">
              <Chip 
                label="LIVE" 
                color="error" 
                size="small"
                sx={{ animation: 'pulse 2s infinite' }}
              />
            </Badge>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={enabled ? 'outlined' : 'contained'}
            color={enabled ? 'error' : 'primary'}
            startIcon={enabled ? <StopIcon /> : <PlayIcon />}
            onClick={onToggle}
          >
            {enabled ? 'Stop' : 'Start'} Monitoring
          </Button>
        </Box>
      </Box>

      {/* Status Alert */}
      {enabled ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Monitoring live requests... {requests.length} requests captured
            </Typography>
          </Box>
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Real-time monitoring is disabled. Click "Start Monitoring" to begin.
        </Alert>
      )}

      {/* Request Stream */}
      <Paper sx={{ p: 2, height: 600, overflow: 'hidden' }}>
        {requests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              {enabled ? 'Waiting for requests...' : 'No requests to display'}
            </Typography>
          </Box>
        ) : (
          <Box ref={listRef} sx={{ height: '100%', overflow: 'auto' }}>
            <List>
              {requests.map((request, index) => (
                <React.Fragment key={`${request.timestamp}-${index}`}>
                  <ListItem
                    sx={{
                      bgcolor: index === 0 && enabled ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.3s'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {format(new Date(request.timestamp), 'HH:mm:ss.SSS')}
                          </Typography>
                          <Chip
                            label={getModelShortName(request.model)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={request.request_type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {request.tool_name && (
                            <Chip
                              label={request.tool_name}
                              size="small"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          )}
                          <Chip
                            label={`${request.total_tokens} tokens`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={formatCost(request.cost)}
                            size="small"
                            variant="outlined"
                            color={request.cost > 0.01 ? 'warning' : 'default'}
                          />
                          <Chip
                            label={`${request.latency_ms}ms`}
                            size="small"
                            variant="outlined"
                          />
                          {request.success ? (
                            <Chip
                              label="Success"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Failed"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {request.cache_hit && (
                            <Chip
                              label="Cached"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            User: {request.user_id}
                          </Typography>
                          {request.reasoning && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {request.reasoning}
                            </Typography>
                          )}
                          {request.error_message && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                              Error: {request.error_message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < requests.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
    </Box>
  );
};

export default RealTimeMonitor;